import { SceneResizeHandle } from '@src/components/timeline/controls/SceneResizeHandle';
import { useEditor } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { type ElementBoundary } from '@src/utility/elementSnapping';
import { LogManager } from '@src/utility/Logger';
import {
  type ElementTiming,
  resolveSceneRange,
  type SceneRange,
} from '@src/utility/sceneAnchors';
import { buildSceneBoundaryAnchorUpdates } from '@src/utility/sceneBoundaryAdjustments';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { PADDING_LEFT_COMPENSATION } from './TimelineElement';

import type { Scene } from '@src/editor/models/layout/Scene';
import type { CSSProperties } from 'react';

interface TimelineSceneBoundaryProps {
  leftScene: Scene;
  rightScene: Scene;
  elementTimingMap: Map<string, ElementTiming>;
  previewRanges?: Record<string, SceneRange>;
  onPreviewRanges?: (updates: Record<string, SceneRange | null>) => void;
  isViewOnly?: boolean;
}

const MIN_PIXEL_OFFSET = 0;
const MIN_SCENE_DURATION = 0.5;
const HANDLE_OFFSET = 4;
const logger = LogManager.getLogger('component:TimelineSceneBoundary');

export const TimelineSceneBoundary = observer(
  ({
    leftScene,
    rightScene,
    elementTimingMap,
    previewRanges = {},
    onPreviewRanges,
    isViewOnly = false,
  }: TimelineSceneBoundaryProps) => {
    const videoCreator = useVideoCreatorStore();
    const editorStore = useEditor();
    const timelineScale = videoCreator.timelineScale;
    const durationSeconds = Number(videoCreator.duration) || 0;

    const originalElementTimings = useMemo(() => {
      return Array.from(elementTimingMap.values());
    }, [elementTimingMap]);

    const snapBoundaries = useMemo<ElementBoundary[]>(() => {
      return originalElementTimings.flatMap(timing => [
        { time: timing.start, type: 'start', elementId: timing.elementId },
        { time: timing.end, type: 'end', elementId: timing.elementId },
      ]);
    }, [originalElementTimings]);

    const leftRange = useMemo(
      () => resolveSceneRange(leftScene, elementTimingMap, durationSeconds),
      [leftScene, elementTimingMap, durationSeconds],
    );
    const rightRange = useMemo(
      () => resolveSceneRange(rightScene, elementTimingMap, durationSeconds),
      [rightScene, elementTimingMap, durationSeconds],
    );

    const effectiveLeftRange = previewRanges[leftScene.id] ?? leftRange;
    const effectiveRightRange = previewRanges[rightScene.id] ?? rightRange;
    const boundaryTime = Math.min(
      effectiveLeftRange.end,
      effectiveRightRange.start,
    );
    const left = Math.max(
      PADDING_LEFT_COMPENSATION + boundaryTime * timelineScale,
      MIN_PIXEL_OFFSET,
    );

    const clamp = useMemo(() => {
      return {
        min: Math.max(effectiveLeftRange.start + MIN_SCENE_DURATION, 0),
        max: Math.min(
          effectiveRightRange.end - MIN_SCENE_DURATION,
          durationSeconds,
        ),
      };
    }, [effectiveLeftRange.start, effectiveRightRange.end, durationSeconds]);

    const handlePreview = useCallback(
      (nextTime: number) => {
        if (!onPreviewRanges) {
          return;
        }
        onPreviewRanges({
          [leftScene.id]: {
            start: effectiveLeftRange.start,
            end: nextTime,
          },
          [rightScene.id]: {
            start: nextTime,
            end: effectiveRightRange.end,
          },
        });
      },
      [
        effectiveLeftRange.start,
        effectiveRightRange.end,
        leftScene.id,
        onPreviewRanges,
        rightScene.id,
      ],
    );

    const clearPreviewImmediate = useCallback(() => {
      if (!onPreviewRanges) {
        return;
      }
      onPreviewRanges({
        [leftScene.id]: null,
        [rightScene.id]: null,
      });
    }, [leftScene.id, onPreviewRanges, rightScene.id]);

    const clearPreviewNextFrame = useCallback(() => {
      if (typeof window === 'undefined' || !window.requestAnimationFrame) {
        clearPreviewImmediate();
        return;
      }

      window.requestAnimationFrame(() => {
        clearPreviewImmediate();
      });
    }, [clearPreviewImmediate]);

    const pendingClearRef = useRef<number | null>(null);

    useEffect(() => {
      const pending = pendingClearRef.current;
      if (pending == null) {
        return;
      }

      // Use base ranges (source of truth) rather than effective ranges
      // (which include stale previews) to detect when committed data
      // has propagated. Once the base boundary matches the pending commit
      // time, we know the source reflects the change and can clear previews.
      const baseBoundary = Math.min(leftRange.end, rightRange.start);

      if (Math.abs(baseBoundary - pending) <= 1 / 1000) {
        pendingClearRef.current = null;
        clearPreviewImmediate();
      }
    }, [clearPreviewImmediate, leftRange.end, rightRange.start]);

    const handleCommit = useCallback(
      (nextTime: number) => {
        const updates = buildSceneBoundaryAnchorUpdates(leftScene, rightScene, {
          boundaryTime: nextTime,
          elementTimings: originalElementTimings,
        });

        if (!updates) {
          logger.warn('Failed to build scene anchor updates for boundary drag');
          clearPreviewNextFrame();
          return;
        }

        if (!editorStore.editor) {
          logger.warn('Editor store unavailable for boundary drag');
          clearPreviewNextFrame();
          return;
        }

        const commandPayload: Array<{
          sceneId: string;
          updates: Record<string, unknown>;
        }> = [];

        updates.forEach(update => {
          const anchorUpdates: Record<string, unknown> = {};
          if (update.anchors.start) {
            anchorUpdates.anchorStart = update.anchors.start;
          }
          if (update.anchors.end) {
            anchorUpdates.anchorEnd = update.anchors.end;
          }

          if (Object.keys(anchorUpdates).length > 0) {
            commandPayload.push({
              sceneId: update.sceneId,
              updates: anchorUpdates,
            });
          }
        });

        if (commandPayload.length === 0) {
          logger.warn('Boundary drag produced no command updates');
          clearPreviewNextFrame();
          return;
        }

        pendingClearRef.current = nextTime;
        const success = editorStore.editor.commandUpdateScenes(commandPayload);
        if (!success) {
          logger.warn('commandUpdateScenes failed for boundary drag');
          pendingClearRef.current = null;
          clearPreviewNextFrame();
          return;
        }
      },
      [
        clearPreviewNextFrame,
        editorStore.editor,
        leftScene,
        originalElementTimings,
        rightScene,
      ],
    );

    const handleStyle: CSSProperties = {
      left:
        PADDING_LEFT_COMPENSATION +
        boundaryTime * timelineScale -
        HANDLE_OFFSET,
      top: 4,
      height: 26,
    };

    const handleDisabled = clamp.max - clamp.min <= 0 || isViewOnly;

    return (
      <>
        <BoundaryRoot style={{ left }}>
          <BoundaryIndicator />
        </BoundaryRoot>
        {!handleDisabled && (
          <SceneResizeHandle
            side="end"
            time={boundaryTime}
            clamp={clamp}
            snapToFrame={time => editorStore.editor.snapTimeToFrame(time)}
            snapBoundaries={snapBoundaries}
            onPreview={handlePreview}
            onCommit={handleCommit}
            style={handleStyle}
          />
        )}
      </>
    );
  },
);

const BoundaryRoot = styled.div`
  position: absolute;
  top: 4px;
  height: 26px;
  width: 12px;
  margin-left: -6px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BoundaryIndicator = styled.div`
  width: 2px;
  height: 18px;
  background: #ffffff0d;
  border-radius: 1px;
`;
